import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

import { ColumnsConfig, SimTableId, TableDataKey } from 'sim-management-frontend/dist/sim-management';

import { CommonService } from 'src/app/shared/services/common.service';
import { DeviceConstantsService } from 'src/app/dashboard/devices/device-constants.service';

/**
 * Everything VMSPRO has to tell the `sim-management` library about its own tables.
 *
 * The library draws five tables but owns no column list: it asks `SctTableConfigService` for a
 * preset per tab and calls each column's `formatter(value, row, component)` to build the cell.
 * `component` is the library component, which is how the coloured Data Usage cell reaches
 * `getClass(row)` — the colour comes from `row.log_level`, which the daily report job writes.
 *
 * Column keys are the row fields of the `cellular/*` endpoints. Nothing here calls the API.
 */

const BYTES_PER_KB = 1024;

/** What an empty cell shows, matching what the library already prints for a missing date. */
const NO_VALUE = '---';

/**
 * Formats a date-only value carries no time zone, so parsing it as ISO would place it at
 * midnight UTC and then shift it a day for anyone east of UTC. These formats are tried in order
 * and parsed as UTC, which keeps the calendar day the backend meant.
 */
const DAY_FORMATS: (string | moment.MomentBuiltinFormat)[] = ['YYYY-MM-DD', 'MM/DD/YYYY', moment.ISO_8601];

/** Bytes as KB, two decimals, as a number so the column still sorts and filters numerically. */
export function formatKb(bytes: unknown): number | null {
	const value = Number(bytes);
	if (bytes === null || bytes === undefined || Number.isNaN(value))
		return null;

	return +(value / BYTES_PER_KB).toFixed(2);
}

/**
 * A KB cell. `value` keeps both decimals as text, so the screen and the export read `0.00` as
 * the old screen did — as a number, `formatKb` alone prints `0`. Sorting and the number filter
 * read the number in `sortValue`.
 */
export function kbCell(bytes: unknown): TableDataKey {
	const kb = formatKb(bytes);

	return kb === null ? { value: null, sortValue: null } : { value: kb.toFixed(2), sortValue: kb };
}

/**
 * Rounds a value that is *already* in MB to two decimals.
 *
 * The bundle quota fields (`quota_remaining_volume`, `quota_total_volume`) come from 1nce in MB,
 * its native unit, and the backend passes them through unchanged — unlike the usage fields,
 * which the backend normalises to bytes. So there is nothing to divide here.
 */
export function formatMb(megabytes: unknown): number | null {
	const value = Number(megabytes);
	if (megabytes === null || megabytes === undefined || megabytes === '' || Number.isNaN(value))
		return null;

	return +value.toFixed(2);
}

/**
 * The cells and columns the SIM tables share, and that the Usage History header reuses
 * (`buildSimHistoryHeader()`), so a column reads the same wherever it appears.
 *
 * Injected services are passed in rather than injected here, because this runs from an
 * `app.module.ts` factory where there is no injection context.
 */
function simColumns(
	translate: TranslateService,
	commonService: CommonService,
	deviceConstants: DeviceConstantsService
) {
	const t = (key: string) => translate.instant(key);

	/**
	 * Shifts a time into the signed-in user's time zone.
	 *
	 * `showFieldBasedOnTimeZone` works in unix seconds, but the SIM SDK sends
	 * `last_disabled_time`, `last_blocked_time` and friends as ISO strings, and the device side
	 * sends numbers. Both are accepted here, the same way `devices.component.ts` and
	 * `restart-count-report.component.ts` convert before calling it.
	 */
	const userTime = (timestamp: unknown): number | null => {
		if (timestamp === null || timestamp === undefined || timestamp === '')
			return null;

		const unix = typeof timestamp === 'number' ? timestamp : moment.utc(timestamp as moment.MomentInput).unix();
		if (!unix || Number.isNaN(unix))
			return null;

		return commonService.showFieldBasedOnTimeZone(unix);
	};

	/**
	 * A time cell. A missing or unparseable time shows `---` instead of an epoch date.
	 *
	 * Emitted in **milliseconds**. A `timestamp` column is rendered with `moment(value)`, and
	 * moment reads a bare number as milliseconds — handing it the seconds that
	 * `showFieldBasedOnTimeZone` returns dated every row to January 1970.
	 */
	const userTimeCell = (timestamp: unknown): TableDataKey => {
		const shifted = userTime(timestamp);
		return shifted === null ? { value: null, displayValue: NO_VALUE } : { value: shifted * 1000 };
	};

	/** `Disabled Since (UTC+03:00)` — the suffix says which zone the column is rendered in. */
	const withTimeZone = (name: string) => `${name} ${commonService.showUserTimeZoneReference()}`;

	/** Device stages are numbers on the wire; the options carry the translation key per stage. */
	const stageLabel = (stage: unknown): string => {
		const option = deviceConstants.deviceStageOptions.find((item) => item.value === +stage);
		return option ? t(option.label) : NO_VALUE;
	};

	/**
	 * The stage segment of a `/device-page/:stage/:id` URL.
	 *
	 * `DeviceRouteGuard` matches the URL against stage labels with their spaces hyphenated
	 * (`getStageSlugs()`), so a raw label leaves `SCT Inventory` and `Demos and Engineering`
	 * unmatched — `canMatch` fails, the route never activates, and the link reads as dead. Every
	 * other link builder in the app does the same `split(' ').join('-')`.
	 */
	const stageSlug = (stage: unknown): string => stageLabel(stage).split(' ').join('-');

	// A row with no `stage` would slug to `---`, which the guard also rejects, so the link is
	// left off rather than rendered dead.
	const deviceLink = (row: any): any[] | undefined =>
		(row?.imei && row?.stage !== undefined && row?.stage !== null ? ['/device-page', stageSlug(row.stage), row.imei] : undefined);

	const iccidLink = (row: any): any[] | undefined =>
		(row?.imei && row?.stage !== undefined && row?.stage !== null ? ['/device-page', stageSlug(row.stage), row.imei, 'cell-info'] : undefined);

	/**
	 * A boolean cell reads `trueText`/`falseText`, not `displayValue` — see the IMEI Status
	 * column. This looked right only because the table falls back to its own "Yes"/"No" when
	 * they are absent, so the label was never actually coming from here.
	 */
	const yesNo = (value: unknown): TableDataKey => ({
		value: !!value,
		trueText: t('g.yes'),
		falseText: t('g.no')
	});

	/**
	 * A day cell: `MM/DD/YYYY` on screen, `YYYY-MM-DD` in `sortValue`. A `date` column's range
	 * filter and its sort both read `sortValue`, and both need a form moment parses without
	 * guessing — sorted as text, `MM/DD/YYYY` would also put December before the next January.
	 */
	const dayOnly = (value: unknown): TableDataKey => {
		if (!value || value === NO_VALUE)
			return { value: NO_VALUE };

		const parsed = typeof value === 'number' ? moment.unix(value).utc() : moment.utc(value as string, DAY_FORMATS);
		return parsed.isValid() ? { value: parsed.format('MM/DD/YYYY'), sortValue: parsed.format('YYYY-MM-DD') } : { value: NO_VALUE };
	};

	// -- columns reused across tabs ------------------------------------------------------------

	const serialNumber = (): ColumnsConfig => ({
		key: 'serial_number',
		name: t('g.serial_number'),
		type: 'string'
	});

	const imeiColumn = (): ColumnsConfig => ({
		key: 'imei',
		name: t('devices.imei'),
		type: 'link',
		formatter: (value, row) => ({ value, link: deviceLink(row) })
	});

	const categoryColumn = (): ColumnsConfig => ({
		key: 'stage',
		name: t('sim_management.category'),
		type: 'string',
		formatter: (value) => ({ value: stageLabel(value), sortValue: +value })
	});

	// `cellular_iccid` is also the field the library reads to know which rows are selected, so
	// the cell has to carry the raw ICCID in `value`.
	const iccidColumn = (): ColumnsConfig => ({
		key: 'cellular_iccid',
		name: t('qv.cellular_iccid'),
		type: 'link',
		formatter: (value, row) => ({ value, link: iccidLink(row) })
	});

	const dataUsageColumn = (): ColumnsConfig => ({
		key: 'data_usage',
		name: t('sim_management.data_usage_kb'),
		type: 'number',
		formatter: (value, row, component) => ({
			...kbCell(value),
			// The library owns the colour scale (row.log_level -> colour), so it is asked.
			backGroundColor: component?.getClass ? component.getClass(row) : undefined
		})
	});

	const reconnectTimeColumn = (): ColumnsConfig => ({
		key: 'reconnect_time',
		name: t('sim_management.cellular_reconnect_time'),
		type: 'number'
	});

	const attemptedFwUpdateColumn = (): ColumnsConfig => ({
		key: 'updating_fw',
		name: t('sim_management.attempted_fw_update'),
		type: 'boolean',
		filterTrueText: t('g.yes'),
		filterFalseText: t('g.no'),
		formatter: (value) => yesNo(value)
	});

	return {
		t,
		withTimeZone,
		userTimeCell,
		dayOnly,
		deviceLink,
		serialNumber,
		imeiColumn,
		categoryColumn,
		iccidColumn,
		dataUsageColumn,
		reconnectTimeColumn,
		attemptedFwUpdateColumn
	};
}

/** Builds the five column presets. */
export function buildSimPresets(
	translate: TranslateService,
	commonService: CommonService,
	deviceConstants: DeviceConstantsService
): Record<SimTableId, ColumnsConfig[]> {
	const {
		t,
		withTimeZone,
		userTimeCell,
		dayOnly,
		deviceLink,
		serialNumber,
		imeiColumn,
		categoryColumn,
		iccidColumn,
		dataUsageColumn,
		reconnectTimeColumn,
		attemptedFwUpdateColumn
	} = simColumns(translate, commonService, deviceConstants);

	return {
		usage: [
			serialNumber(),
			imeiColumn(),
			categoryColumn(),
			iccidColumn(),
			dataUsageColumn(),
			{
				key: 'avg_7d_usage',
				name: t('sim_management.daily_avg_last_7_days'),
				type: 'number',
				formatter: (value) => kbCell(value)
			},
			{
				key: 'avg_30d_usage',
				name: t('sim_management.daily_avg_last_30_days'),
				type: 'number',
				formatter: (value) => kbCell(value)
			},
			attemptedFwUpdateColumn(),
			{
				key: 'disabled_count',
				name: t('sim_management.automatic_disable_count'),
				type: 'number'
			},
			{
				key: 'sim_status',
				name: t('sim_management.iccid_status'),
				type: 'string'
			},
			{
				key: 'imei_lock',
				name: t('sim_management.imei_status'),
				type: 'boolean',
				// A lock is not a yes/no question — the column reads "Locked"/"Unlocked", which is
				// what the old screen showed and what the filter offers.
				filterTrueText: t('sim_management.imei_locked'),
				filterFalseText: t('sim_management.imei_unlocked'),
				// A boolean cell renders `trueText`/`falseText` off the cell itself — the column's
				// `filterTrueText`/`filterFalseText` only label the filter dropdown, and
				// `displayValue` is not read at all for this type.
				formatter: (value) => ({
					value: !!value,
					trueText: t('sim_management.imei_locked'),
					falseText: t('sim_management.imei_unlocked')
				})
			},
			{
				// 1NCE reports the IMEI it sees on the SIM, which is not always the device's own —
				// a mismatch is how a swapped or misfitted SIM shows up.
				key: 'imei_reported_by_1nce',
				name: t('sim_management.imei_reported_by_1nce'),
				type: 'string',
				hidden: true,
				noValueString: NO_VALUE
			},
			{
				key: 'fw_version',
				name: t('g.fw_version'),
				type: 'string'
			},
			reconnectTimeColumn(),
			// Hidden by default, still exportable and still switchable from the column control.
			{
				// 1nce reports bundle quota in MB and the backend passes it through untouched, so
				// this is only rounded — never divided. The usage columns above are bytes.
				key: 'quota_remaining_volume',
				name: t('sim_management.bundle_remaining_data_mb'),
				type: 'number',
				hidden: true,
				noValueString: NO_VALUE,
				formatter: (value) => ({ value: formatMb(value) })
			},
			{
				key: 'last_connect_time',
				name: withTimeZone(t('devices.last_connect_time')),
				type: 'timestamp',
				hidden: true,
				formatter: (value) => userTimeCell(value)
			},
			{
				// The library rewrites `row.usage_date` to `MM/DD/YYYY` before the formatters run.
				key: 'usage_date',
				name: t('sim_management.usage_date'),
				type: 'string',
				hidden: true
			},
			{
				key: 'bundle_expiry_date',
				name: t('sim_management.bundle_expiry_date'),
				type: 'string',
				hidden: true,
				formatter: (value) => dayOnly(value)
			},
			// {
			// 	key: 'installation_date',
			// 	name: t('sim_management.installation_date'),
			// 	type: 'string',
			// 	hidden: true,
			// 	formatter: (value) => dayOnly(value)
			// },
			// {
			// 	key: 'debug_enabled',
			// 	name: t('sim_management.debug_enabled'),
			// 	type: 'boolean',
			// 	hidden: true,
			// 	filterTrueText: t('g.yes'),
			// 	filterFalseText: t('g.no'),
			// 	formatter: (value) => yesNo(value)
			// },
			// Opens the per-device usage chart. The library exposes `showUsageHistory(row)` but
			// never calls it itself — the trigger is the host's to supply, and this is it. It has
			// to stay last: an `icon` column is neither sortable, filterable nor exportable, so
			// it reads as a trailing actions column rather than a gap mid-table.
			{
				key: 'usage_history',
				name: t('sim_management.usage_history'),
				type: 'icon',
				formatter: (_value, row, component) => ({
					value: '',
					icon: 'history',
					iconColor: '#28a745',
					// `row` is the raw SIM row, which is what `showUsageHistory` expects — the
					// table hands the formatted cell to `action`, not the row behind it.
					action: () => component?.showUsageHistory?.(row)
				})
			}
		],
		disabled: [
			imeiColumn(),
			serialNumber(),
			categoryColumn(),
			iccidColumn(),
			{
				key: 'last_disabled_time',
				name: withTimeZone(t('sim_management.disabled_since')),
				type: 'timestamp',
				formatter: (value) => userTimeCell(value)
			},
			{
				key: 'disabled_count',
				name: t('sim_management.automatic_disable_count'),
				type: 'number'
			},
			{
				key: 'disabled_by',
				name: t('sim_management.disabled_by'),
				type: 'string'
			}
		],
		blocked: [
			serialNumber(),
			imeiColumn(),
			categoryColumn(),
			iccidColumn(),
			{
				key: 'last_blocked_time',
				name: withTimeZone(t('sim_management.blocked_since')),
				type: 'timestamp',
				formatter: (value) => userTimeCell(value)
			}
		],
		history: [
			dataUsageColumn(),
			{
				// `date`, not `string`: the table then filters it as a from/to range, as the old
				// screen did, instead of a text box (VMSPRO-593).
				key: 'usage_date',
				name: t('sim_management.usage_date'),
				type: 'date',
				formatter: (value) => dayOnly(value)
			},
			reconnectTimeColumn(),
			attemptedFwUpdateColumn(),
			{
				// Why a disabled day was disabled: the backend's `disable_reasons` codes, one line
				// each. A day the SIM was not disabled lists nothing (VMSPRO-591).
				key: 'disable_reasons',
				name: t('sim_management.disable_reason'),
				type: 'list',
				formatter: (value) => ({
					value: (Array.isArray(value) ? value : []).map((code: string) => ({ text: t(`sim_management.${code}`) }))
				})
			}
		],
		missingFromOnce: [
			{
				// Only this tab links the Serial Number to the device page, as the old screen did.
				...serialNumber(),
				type: 'link',
				formatter: (value, row) => ({ value, link: deviceLink(row) })
			},
			imeiColumn(),
			categoryColumn(),
			iccidColumn()
		]
	};
}

/**
 * The Usage History header, as the old SIM screen showed it (VMSPRO-591): IMEI, Serial Number,
 * ICCID, 140 KB Exceedance Count and Category. Without it the library falls back to its own
 * header, which has no IMEI or count and prints the raw stage number as "Group Name".
 *
 * The library reads each entry like a column against the usage row the popup was opened for, and
 * prints `---` for an empty value.
 */
export function buildSimHistoryHeader(
	translate: TranslateService,
	commonService: CommonService,
	deviceConstants: DeviceConstantsService
): ColumnsConfig[] {
	const { t, serialNumber, imeiColumn, categoryColumn, iccidColumn } = simColumns(translate, commonService, deviceConstants);

	// The old dialog opened its IMEI and ICCID links in a new tab, keeping the dialog open.
	const inNewTab = (column: ColumnsConfig): ColumnsConfig => ({
		...column,
		formatter: (value, row, component) => ({ ...column.formatter!(value, row, component), target: '_blank' })
	});

	return [
		inNewTab(imeiColumn()),
		serialNumber(),
		inNewTab(iccidColumn()),
		{
			key: 'days_exceeded_140k',
			name: t('sim_management.days_exceeded_140k_count'),
			type: 'number',
			headerTooltip: t('tooltip.sim_days_exceeded_140k_count')
		},
		categoryColumn()
	];
}

/**
 * `tableId` per tab. The library hands each one to `sct-custom-table`, which stores the user's
 * column order and visibility under it — so they must differ per tab or the tabs overwrite each
 * other. They double as the export names Jira asked for (VMSPRO-544).
 */
export const SIM_TABLE_IDS: Partial<Record<SimTableId, string>> = {
	usage: 'All SIMs Usage',
	disabled: 'Disabled SIMs',
	blocked: 'Blocked SIMs',
	history: 'Usage History',
	missingFromOnce: 'SIMs missing from 1nce account'
};
